// React
import { type ReactNode } from 'react';

// UI primitives
import { cn } from '@/lib/utils';
import { FIELD_TRIGGER_CLASSES } from '@/components/ui/field-trigger';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Icons
import { ChevronDownIcon } from '../../react/components/icons/ChevronDownIcon';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface StatusDropdownOption {
  label: string;
  activeTextClassName: string;
  inactiveTextClassName: string;
  icon: (isActive: boolean) => ReactNode;
}

export type StatusDropdownConfigMap<T extends string> = Record<T, StatusDropdownOption>;

export interface StatusDropdownProps<T extends string> {
  value: T;
  config: StatusDropdownConfigMap<T>;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Pickable states, when narrower than `config`. Defaults to every key. */
  selectable?: readonly T[];
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function StatusDropdown<T extends string>({
  value,
  config,
  onChange,
  disabled = false,
  selectable,
  className,
}: StatusDropdownProps<T>) {
  const currentStatus = config[value];
  if (!currentStatus) return null;

  const options = (Object.entries(config) as [T, StatusDropdownOption][]).filter(
    ([key]) => selectable === undefined || selectable.includes(key),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            FIELD_TRIGGER_CLASSES,
            'w-[194px] px-[18px] py-[12px] shadow-soft select-none group',
            'text-[15px] font-semibold',
            // Cancel out FIELD_TRIGGER_CLASSES' grey/yellow border states so
            // the trigger keeps the canonical dinnerplate-white outline in
            // every interaction state (no yellow flash on focus, no grey
            // tint on hover).
            'hover:border-dinnerplate-white focus:border-dinnerplate-white focus-visible:border-dinnerplate-white',
            'focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0',
            currentStatus.activeTextClassName,
            className,
          )}
        >
          <span className="flex items-center gap-[6px]">
            {currentStatus.icon(true)}
            <span>{currentStatus.label}</span>
          </span>
          <ChevronDownIcon className="shrink-0 text-off-black transition-transform duration-200 group-data-[state=open]:rotate-180" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="flex w-[var(--radix-dropdown-menu-trigger-width)] flex-col gap-[6px] p-[6px]"
      >
        {options.map(([statusKey, statusOption]) => {
          const isActive = statusKey === value;

          return (
            <DropdownMenuItem
              key={statusKey}
              onClick={() => onChange(statusKey)}
              className={cn(
                'gap-[6px] p-[12px] rounded-[8px] text-[15px]',
                // Every row keeps its brand colour at rest — only the
                // background changes between hover (lighter) and selected
                // (light). Override DropdownMenuItem's default which uses
                // -light for both hover and highlight.
                'hover:bg-dinnerplate-white-lighter focus:bg-dinnerplate-white-lighter data-[highlighted]:bg-dinnerplate-white-lighter',
                isActive &&
                  'bg-dinnerplate-white-light hover:bg-dinnerplate-white-light focus:bg-dinnerplate-white-light data-[highlighted]:bg-dinnerplate-white-light',
              )}
            >
              {statusOption.icon(isActive)}
              <span
                className={cn(
                  'font-inter text-[15px] font-semibold',
                  statusOption.activeTextClassName,
                )}
              >
                {statusOption.label}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
